import Parse from 'parse'
import { ETFFilterState, ETFPeriod } from '@/@types/etf'
import { ManagementStrategy } from '@/constants/filterOptions'

class ETFsApi {
  async getMany(values: { filter: ETFFilterState; sort?: string; page?: number; pageSize?: number; period?: ETFPeriod; watchlistOnly?: boolean }) {
    // Transform frontend filter model to match the server's format
    const serverParams = {
      // Sort parameter
      sort: values.sort || '-aum',

      // Pagination - convert page-based to skip-based pagination
      limit: values.pageSize || 20,
      skip: values.page ? (values.page - 1) * (values.pageSize || 20) : 0,

      // Filter parameters
      isWatchlist: values.watchlistOnly,

      // Convert filter object properties to the expected server parameters
      assetClass: values.filter.assetClasses,
      zone: values.filter.geographies,
      theme: values.filter.themes,
      sectors: values.filter.sectors,
      providers: values.filter.providers,
      strategy: values.filter.strategies,
      currency: values.filter.currencies,

      // Handle ESG filtering - convert enum to boolean
      isESG: values.filter.esgType === 'esg' ? true : values.filter.esgType === 'nonEsg' ? false : undefined,

      // Handle management strategy (active/passive)
      managementStrategy: values.filter.managementStrategies?.length
        ? this.convertManagementStrategiesToNumbers(values.filter.managementStrategies)
        : undefined,

      searchString: values.filter.searchString
    }

    // Remove undefined properties to avoid validation issues
    const cleanParams = Object.fromEntries(Object.entries(serverParams).filter(([_, value]) => value !== undefined))

    console.log('API params sent to server:', cleanParams)
    return Parse.Cloud.run('Etf_getMany', cleanParams)
  }

  // Helper method to convert string strategy types to numeric values expected by server
  private convertManagementStrategiesToNumbers(strategies: string[]): number[] {
    const mappings: Record<string, number> = {
      Active: ManagementStrategy.ACTIVE,
      Passive: ManagementStrategy.PASSIVE
    }
    return strategies.map((strategy) => mappings[strategy]).filter((num) => num !== undefined) // Remove any undefined values
  }

  async getOne(values: { id: string }) {
    return Parse.Cloud.run('Etf_getOne', values)
  }

  async getWatchlist() {
    return Parse.Cloud.run('Admin_getETFWatchlist')
  }

  async getFilterOptions() {
    return Parse.Cloud.run('Etf_getOptions')
  }

  /**
   * Get performance data for an ETF
   * @param values - Object containing:
   *   - id: The ID of the ETF
   *   - startDate: The start date in YYYY-MM-DD format
   *   - endDate: The end date in YYYY-MM-DD format
   * @returns Promise<any> - Performance data
   */
  async getPerformance(values: { id: string; startDate: string; endDate: string }) {
    return Parse.Cloud.run('Etf_getPerformance', values)
  }

  /**
   * Get ETF data formatted for comparison
   * @param values - Object containing:
   *   - id: The ID of the ETF to fetch for comparison
   * @returns Promise<ETFForComparison> - ETF data formatted for comparison
   */
  async getOneForComparison(values: { id: string }) {
    return Parse.Cloud.run('Etf_getOneForComparison', values)
  }

  /**
   * Get all ETFs with basic information (for search/dropdown)
   * @returns Promise<ETFListItem[]> - Array of ETFs with basic info
   */
  async getAll() {
    return Parse.Cloud.run('Etf_getAll')
  }

  /**
   * Get top performing tickers
   * @returns Promise<{ topTickers: { ticker: string, objectId: string, openClosePercentChange: number | null, close: number | null }[] }> - Top tickers with market data
   */
  async getTopTickers() {
    return Parse.Cloud.run('Etf_getTopTickers')
  }
}

export default new ETFsApi()
